import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Typography, Divider, Button, Box, Tabs, Tab, FormControlLabel, Switch, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import CommonLoader from "../../UiComponents/CommonLoader/CommonLoader";
import ImageViewerDialog from "../UserWallet/components/ImageViewerDialog";
import { GetClientWalletDetail, getReceiptImage } from "../../Service/_index";
import { WALLET_INSURERS } from "../../Shared/CommonConstant";
import WalletTransactionDialog from "./WalletTransactionDialog";
import SetMinimumBalanceDialog from "./SetMinimumBalanceDialog";
import WalletStatementTable from "./WalletStatementTable";
import { ledgerWarning } from "./walletEntryUtils";
import { isAtOrBelowMinimum } from "../../Shared/walletMinimum";
import { requestWalletAlertsRefresh } from "../Notification/useWalletAlerts";
import Styles from "./ClientCompanyWallet.module.scss";

const money = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const TABS = ["All", ...WALLET_INSURERS];
const STATEMENT_PANEL_ID = "wallet-statement-panel";

const ClientWalletDetail = ({ clientId, showBackLink = true }) => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  const [grouped, setGrouped] = useState(true);
  const [statementVersion, setStatementVersion] = useState(0);
  const [dialogState, setDialogState] = useState(null); // { company, mode: "topup" | "remove" } | null
  const [minBalanceCompany, setMinBalanceCompany] = useState(null); // company name | null
  const [imageViewDialog, setImageViewDialog] = useState({ open: false, imageUrl: "", fileName: "" });
  const walletRequest = useRef(0);

  const loadWallet = useCallback(() => {
    const id = ++walletRequest.current;
    setLoading(true);
    GetClientWalletDetail(clientId)
      .then((data) => {
        if (id === walletRequest.current) setWallet(data); // an older answer never overwrites a newer one
      })
      .finally(() => {
        if (id === walletRequest.current) setLoading(false);
      });
  }, [clientId]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  // A top-up or removal puts a new entry at the top of the statement: reload the cards and start the statement again
  // from its first page (the table owns its own page, so a new key is what resets it).
  const refreshAll = () => {
    loadWallet();
    setStatementVersion((n) => n + 1);
    requestWalletAlertsRefresh(); // a top-up or removal can clear or raise a low-balance alert in the navbar bell
  };

  const handleViewReceipt = async (receiptImage) => {
    const response = await getReceiptImage(receiptImage.key);
    if (!response?.error && response?.url) {
      setImageViewDialog({
        open: true,
        imageUrl: response.url,
        fileName: receiptImage.fileName || "Receipt",
      });
    }
  };

  const balances = wallet?.balances || {};
  // The super admin is the operator, not a broker: his wallet is not topped up or reduced (the server refuses it too).
  const noMoneyMoves = String(wallet?.roleId || "").trim().toUpperCase() === "SUPER_ADMIN";
  const minimumBalances = wallet?.minimumBalances || {};
  const ledgerDifferences = wallet?.ledgerDifferences || {};

  if (loading && !wallet) {
    return <CommonLoader />;
  }

  if (!wallet) {
    return (
      <Grid container className="Master_Card_Container">
        <Grid item xs={12} className={Styles.AccessDenied}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#101828" }}>Wallet not found</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container className="Master_Card_Container">
      {showBackLink && (
        <Grid item xs={12} className={Styles.DetailTopBar}>
          <Typography className={Styles.BackLink} onClick={() => navigate("/clientCompanyWallet")}>
            <ArrowBackIcon sx={{ fontSize: 16 }} /> Back to Company Wallet
          </Typography>
        </Grid>
      )}

      <Grid item xs={12} className={Styles.WalletBand}>
        <Grid className={Styles.WalletBandRow}>
          <Grid>
            <Typography className={Styles.ClientName}>{wallet.clientName}</Typography>
            {wallet.email && <Typography className={Styles.ClientEmail}>{wallet.email}</Typography>}
          </Grid>
          <Grid>
            <Typography className={Styles.TotalBalanceLabel}>Total Balance</Typography>
            <Typography className={Styles.TotalBalanceValue}>&#8377; {money(wallet.total)}</Typography>
          </Grid>
        </Grid>
      </Grid>

      <Box
        sx={{
          // The parent is a Grid `container` (flexbox) — a plain Box child
          // has no flex-basis, so without an explicit width it shrinks to
          // its content instead of spanning the row like a proper
          // `Grid item xs={12}` would. That's what was capping this grid to
          // less than the full page width.
          width: "100%",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "14px",
        }}
        className={Styles.InsurerGrid}
      >
        {WALLET_INSURERS.map((company) => {
          const balance = Number(balances[company] || 0);
          const minimumBalance = Number(minimumBalances[company] || 0);
          // You set a minimum and the balance has reached it (the same rule the bell uses).
          const isBelowMinimum = isAtOrBelowMinimum(balance, minimumBalance);
          const mismatch = ledgerWarning(ledgerDifferences[company]);

          return (
            <Box
              key={company}
              role="group"
              aria-label={`${company} wallet`}
              sx={{ display: "flex", flexDirection: "column", height: "100%" }}
              className={`${Styles.InsurerCard} ${isBelowMinimum ? Styles.InsurerCardBelowMinimum : ""}`}
            >
              <Box className={Styles.InsurerCardHeader}>{company}</Box>
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }} className={Styles.InsurerCardBody}>
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: "8px" }}>
                  <Typography className={Styles.InsurerAmount}>&#8377; {money(balance)}</Typography>

                  {isBelowMinimum && (
                    <Box
                      sx={{ display: "flex", flexDirection: "row", alignItems: "center", width: "fit-content" }}
                      className={Styles.BelowMinimumChip}
                    >
                      <WarningAmberIcon sx={{ fontSize: 12 }} /> At or below minimum
                    </Box>
                  )}

                  {mismatch && (
                    <Tooltip title={mismatch} arrow describeChild>
                      <Box
                        tabIndex={0}
                        sx={{ display: "flex", flexDirection: "row", alignItems: "center", width: "fit-content" }}
                        className={Styles.BelowMinimumChip}
                      >
                        <WarningAmberIcon sx={{ fontSize: 12 }} /> Does not match history
                      </Box>
                    </Tooltip>
                  )}

                  <Box
                    sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
                    className={Styles.MinBalanceRow}
                  >
                    <span>Minimum: &#8377; {money(minimumBalance)}</span>
                    <Box
                      component="span"
                      role="button"
                      tabIndex={0}
                      aria-label={`Edit minimum balance for ${company}`}
                      sx={{ display: "inline-flex", alignItems: "center" }}
                      className={Styles.MinBalanceEditLink}
                      onClick={() => setMinBalanceCompany(company)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setMinBalanceCompany(company);
                        }
                      }}
                    >
                      <EditIcon sx={{ fontSize: 13 }} />
                    </Box>
                  </Box>
                </Box>

                {noMoneyMoves ? null : (
                <Box
                  sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "8px", mt: "12px" }}
                  className={Styles.InsurerActions}
                >
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    className={Styles.TopupButton}
                    startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                    onClick={() => setDialogState({ company, mode: "topup" })}
                  >
                    Topup
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={balance <= 0}
                    className={Styles.RemoveButton}
                    startIcon={<RemoveIcon sx={{ fontSize: "14px !important" }} />}
                    onClick={() => setDialogState({ company, mode: "remove" })}
                  >
                    Remove
                  </Button>
                </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1 }} />

      <Grid item xs={12} className={Styles.TransactionSection}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mt: 2,
            mb: 1.5,
          }}
        >
          <Typography className="Master_Card_Tittle">Transaction History</Typography>
          {/* describeChild: the hint describes the switch; by default a tooltip would REPLACE its accessible name */}
          <Tooltip
            title="Switch off to see every wallet row on its own, each with its own balance after."
            arrow
            describeChild
          >
            <FormControlLabel
              control={<Switch size="small" checked={grouped} onChange={(event) => setGrouped(event.target.checked)} />}
              label="Group by policy"
              sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: 13, fontWeight: 500, color: "#344054" } }}
            />
          </Tooltip>
        </Box>
        <Tabs
          value={activeTab}
          onChange={(_, next) => setActiveTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Company"
          sx={{
            minHeight: 40,
            mb: 1.5,
            borderBottom: "1px solid #EAECF0",
            "& .MuiTabs-indicator": { backgroundColor: "#D4AF37" },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              minHeight: 40,
              minWidth: 0,
              px: 1.5,
            },
            "& .Mui-selected": { color: "#1E3A5F !important" },
          }}
        >
          {TABS.map((tab) => (
            <Tab key={tab} value={tab} label={tab} id={`wallet-tab-${tab}`} aria-controls={STATEMENT_PANEL_ID} />
          ))}
        </Tabs>
        <Box role="tabpanel" id={STATEMENT_PANEL_ID} aria-labelledby={`wallet-tab-${activeTab}`}>
          <WalletStatementTable
            key={`${clientId}|${activeTab}|${grouped}|${statementVersion}`}
            clientId={clientId}
            companyName={activeTab === "All" ? null : activeTab}
            grouped={grouped}
            onOpenPolicy={(policyId) => navigate(`/onlinePolicy/view/form/${policyId}`)}
            onViewReceipt={handleViewReceipt}
          />
        </Box>
      </Grid>

      {dialogState && (
        <WalletTransactionDialog
          open={!!dialogState}
          mode={dialogState.mode}
          company={dialogState.company}
          clientId={clientId}
          clientName={wallet.clientName}
          currentBalance={balances[dialogState.company]}
          onClose={() => setDialogState(null)}
          onSaved={() => {
            setDialogState(null);
            refreshAll();
          }}
        />
      )}

      {minBalanceCompany && (
        <SetMinimumBalanceDialog
          open={!!minBalanceCompany}
          company={minBalanceCompany}
          clientId={clientId}
          clientName={wallet.clientName}
          currentMinimum={minimumBalances[minBalanceCompany]}
          onClose={() => setMinBalanceCompany(null)}
          onSaved={() => {
            setMinBalanceCompany(null);
            loadWallet();
            requestWalletAlertsRefresh(); // the bell uses this minimum
          }}
        />
      )}

      <ImageViewerDialog
        open={imageViewDialog.open}
        onClose={() => setImageViewDialog({ open: false, imageUrl: "", fileName: "" })}
        imageUrl={imageViewDialog.imageUrl}
        fileName={imageViewDialog.fileName}
      />
    </Grid>
  );
};

export default ClientWalletDetail;
